import Raven from 'raven-js';
import styled from 'foundations-theming/utils';
import { DELIVERY_METHOD_NAMES } from '../../constants';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'I18n';
import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { capabilities } from 'navigation-components/capabilities';
import { useInvokeCapability } from 'navigation-components/consumer/hooks';
import { getTeaseIframeDimensions } from '../../selectors/iframe';
import { getPortalId } from '../../utils/getPortalId';
import DropdownOption from './DropdownOption';
import { turnOffNotification, turnOffNotificationSound } from '../../api/notificationsApi';
import { trackTurnedOffNotificationType } from '../../utils/TrackingUtils';
import { addTeaseAlert } from '../../actions/TeaseActions';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { View } from 'foundations-components/components/View';
import { useBoxShadowHeight } from './hooks/useBoxShadowHeight';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ClickableWrapper = styled.div.withConfig({
  displayName: "DropdownButton__ClickableWrapper",
  componentId: "sc-13u8isu-0"
})(({
  theme,
  isOpen
}) => {
  return {
    position: 'absolute',
    right: theme.space['500'],
    bottom: theme.space['500'],
    width: theme.space['300'],
    height: theme.space['300'],
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&::after': {
      backgroundColor: 'green',
      content: "''",
      display: isOpen ? 'block' : 'none',
      position: 'absolute',
      bottom: theme.space['1700'],
      width: '100%',
      height: theme.space['1500']
    }
  };
});
const Clickable = styled.button.withConfig({
  displayName: "DropdownButton__Clickable",
  componentId: "sc-13u8isu-1"
})(({
  theme,
  showButton,
  smallerClickArea
}) => {
  return {
    color: theme.color['icon-core-subtle'],
    visibility: showButton ? 'visible' : 'hidden',
    width: theme.space['300'],
    height: theme.space['300'],
    border: 'none',
    background: 'none',
    padding: 0,
    margin: 0,
    position: 'relative',
    '&::after': {
      content: "' '",
      borderRadius: '50%',
      left: '50%',
      padding: smallerClickArea ? theme.space['250'] : theme.space['500'],
      position: 'absolute',
      top: '50%',
      transform: 'translate(-50%, -50%)'
    },
    '&:hover': {
      cursor: 'pointer',
      color: theme.color['text-core-default']
    }
  };
});

// @bend-expect trellis-tokenized
const StyledEllipsis = styled.svg.withConfig({
  displayName: "DropdownButton__StyledEllipsis",
  componentId: "sc-13u8isu-2"
})(["color:currentColor;overflow:hidden;path{fill:currentColor;stroke:currentColor;stroke-width:2;}circle{fill:currentColor;stroke:currentColor;}"]);
const StyledDiv = styled.div.withConfig({
  displayName: "DropdownButton__StyledDiv",
  componentId: "sc-13u8isu-3"
})(({
  theme
}) => {
  return {
    lineHeight: theme.typography['heading-100'].lineHeight,
    backgroundColor: theme.color['icon-core-on-fill-default'],
    overflow: 'visible',
    paddingBlock: theme.space['200'],
    paddingInline: '0',
    inlineSize: '320px'
  };
});
const EllipsisIcon = /*#__PURE__*/memo(() =>
/*#__PURE__*/
// @bend-expect trellis-tokenized
_jsx(StyledEllipsis, {
  viewBox: "0 0 130 200",
  xmlns: "http://www.w3.org/2000/svg",
  children: /*#__PURE__*/_jsxs("g", {
    children: [/*#__PURE__*/_jsx("circle", {
      cx: "15",
      cy: "100",
      r: "15"
    }), /*#__PURE__*/_jsx("circle", {
      cx: "65",
      cy: "100",
      r: "15"
    }), /*#__PURE__*/_jsx("circle", {
      cx: "115",
      cy: "100",
      r: "15"
    })]
  })
}));
const DropdownButton = ({
  handleDismissTease,
  notification,
  showButton,
  smallerClickArea
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const portalId = getPortalId();
  const teaseContainerDimensions = useSelector(getTeaseIframeDimensions);
  const changeIframeStyle = useInvokeCapability(capabilities.RESTYLE_CAP);
  const navigateToUrl = useInvokeCapability(capabilities.NAVIGATE_TO_CAP);
  const dispatch = useDispatch();
  const popupRef = useRef(null);
  const showTurnOffNotification = notification.isPreferenceSettable;
  const handleGoToPreferences = useCallback(() => {
    const url = `/notification-preferences/${portalId}/?highlight=${notification.type}`;
    navigateToUrl({
      url,
      openInNewTab: true
    });
    setIsOpen(false);
  }, [navigateToUrl, notification.type, portalId]);
  const handleTurnOffSound = useCallback(() => {
    turnOffNotificationSound({
      id: notification.id,
      deliveryMethod: DELIVERY_METHOD_NAMES.TEASE
    }).catch(e => {
      Raven.captureException(e);
    }).finally(handleDismissTease);
    setIsOpen(false);
    dispatch(addTeaseAlert(notification, 'sound-off'));
  }, [handleDismissTease, notification, dispatch]);
  const handleDontShowAgain = useCallback(() => {
    trackTurnedOffNotificationType(notification, DELIVERY_METHOD_NAMES.TEASE);
    turnOffNotification({
      id: notification.id,
      deliveryMethod: DELIVERY_METHOD_NAMES.TEASE
    }).catch(e => {
      Raven.captureException(e);
    });
    dispatch(addTeaseAlert(notification, 'notification-off'));
    handleDismissTease();
    setIsOpen(false);
  }, [dispatch, handleDismissTease, notification]);
  const boxShadowHeight = useBoxShadowHeight('400', 72);
  useEffect(() => {
    if (!showButton) {
      setIsOpen(false);
    }
  }, [showButton]);
  useLayoutEffect(() => {
    var _popupRef$current;
    const dropDownHeightMaybe = (_popupRef$current = popupRef.current) === null || _popupRef$current === void 0 ? void 0 : _popupRef$current.offsetHeight;
    const dropdownFallback = showTurnOffNotification ? 130 : 110;
    const {
      height,
      width
    } = teaseContainerDimensions;
    const dropdownHeight = dropDownHeightMaybe !== null && dropDownHeightMaybe !== void 0 ? dropDownHeightMaybe : dropdownFallback;
    const heightAdjust = isOpen ? dropdownHeight + boxShadowHeight : 0;
    changeIframeStyle({
      style: {
        height: height + heightAdjust,
        width
      },
      merge: true
    });
  }, [changeIframeStyle, isOpen, showTurnOffNotification, teaseContainerDimensions, boxShadowHeight]);
  const onClickMenuButton = useCallback(() => setIsOpen(prev => !prev), []);
  const DPG = notification.translatedTemplate.translatedDpgName;
  return /*#__PURE__*/_jsx(View, {
    boxShadow: "400",
    children: /*#__PURE__*/_jsx(Popover, {
      placement: 'bottom left',
      autoPlacement: false,
      open: isOpen,
      "data-test-id": "tease-notifications-tooltip",
      content:
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(StyledDiv, {
        children: /*#__PURE__*/_jsxs("div", {
          role: "alertdialog",
          ref: popupRef,
          children: [showTurnOffNotification && /*#__PURE__*/_jsx(DropdownOption, {
            iconName: "block",
            id: "tease-dont-show-again",
            label: i18n.text(DPG ? 'realTime.tease.turnOffNotificationWithDPG' : 'realTime.tease.turnOffNotification', {
              DPG
            }),
            onClick: handleDontShowAgain
          }), /*#__PURE__*/_jsx(DropdownOption, {
            iconName: "volumeMute",
            id: "tease-turn-off-sound",
            label: i18n.text(DPG ? 'realTime.tease.muteNotificationWithDPG' : 'realTime.tease.muteNotification', {
              DPG
            }),
            onClick: handleTurnOffSound
          }), /*#__PURE__*/_jsx(DropdownOption, {
            iconName: "settings",
            id: "tease-go-to-preferences",
            label: i18n.text('realTime.tease.goToPreferences'),
            onClick: handleGoToPreferences
          })]
        })
      }),
      children: /*#__PURE__*/_jsx(ClickableWrapper, {
        isOpen: isOpen,
        children: /*#__PURE__*/_jsx(Clickable, {
          onClick: onClickMenuButton,
          "data-test-id": "tease-more-options-icon",
          "aria-label": i18n.text('realTime.moreOptionsEllipsis'),
          showButton: showButton,
          smallerClickArea: smallerClickArea,
          children: /*#__PURE__*/_jsx(EllipsisIcon, {})
        })
      })
    })
  });
};
export default /*#__PURE__*/memo(DropdownButton);